import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Trash2, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function AdminProgrammeFunding() {
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/public/programme-funding");
            setItems(data.items.map((i) => ({ ...i })));
        } catch (e) { toast.error(formatApiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    function update(idx, field, value) {
        setItems((s) => s.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
    }

    async function save(row) {
        setBusy(true);
        try { await api.post("/admin/programme-funding", row); toast.success("Saved"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    async function remove(key) {
        if (!confirm(`Delete programme "${key}"?`)) return;
        try { await api.delete(`/admin/programme-funding/${key}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    }

    return (
        <div data-testid="admin-programme-funding">
            <div className="gsf-eyebrow">Sponsor a Programme</div>
            <h1 className="mt-2 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold">Programme funding</h1>
            <p className="mt-3 text-sm text-gray-500 max-w-2xl">
                Set targets, current progress and beneficiary counts for each programme. These values power the
                <em> Sponsor a Programme</em> section on the public Donate page. Mark a programme as <em>verified</em>
                only when the figures have been reviewed and approved.
            </p>

            <div className="mt-8 space-y-4">
                {items.map((row, i) => {
                    const pct = row.target > 0 ? Math.min(100, Math.round((row.current / row.target) * 100)) : 0;
                    return (
                        <div key={row.key} className="gsf-card p-6" data-testid={`pf-row-${row.key}`}>
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-serif text-lg font-semibold text-[color:var(--gsf-green-700)]">{row.title}</h3>
                                        {row.verified && (
                                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 flex items-center gap-1"><CheckCircle2 size={12} /> Verified</span>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">{row.key}</p>
                                </div>
                                <div className="flex-1 min-w-[200px] max-w-md">
                                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                        <div className="h-full bg-[color:var(--gsf-green-600)]" style={{ width: `${pct}%` }} />
                                    </div>
                                    <div className="mt-1 text-xs text-gray-500">{pct}% · {row.current} / {row.target || "—"} {row.currency}</div>
                                </div>
                            </div>

                            <div className="mt-5 grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
                                <label className="col-span-2">
                                    <span className="text-xs text-gray-500">Title</span>
                                    <input value={row.title} onChange={(e) => update(i, "title", e.target.value)} data-testid={`pf-title-${i}`} className="w-full mt-1 rounded border border-gray-200 px-2 py-1.5" />
                                </label>
                                <label>
                                    <span className="text-xs text-gray-500">Target</span>
                                    <input type="number" value={row.target} onChange={(e) => update(i, "target", parseInt(e.target.value) || 0)} data-testid={`pf-target-${i}`} className="w-full mt-1 rounded border border-gray-200 px-2 py-1.5" />
                                </label>
                                <label>
                                    <span className="text-xs text-gray-500">Current</span>
                                    <input type="number" value={row.current} onChange={(e) => update(i, "current", parseInt(e.target.value) || 0)} data-testid={`pf-current-${i}`} className="w-full mt-1 rounded border border-gray-200 px-2 py-1.5" />
                                </label>
                                <label>
                                    <span className="text-xs text-gray-500">Beneficiaries</span>
                                    <input type="number" value={row.beneficiaries} onChange={(e) => update(i, "beneficiaries", parseInt(e.target.value) || 0)} data-testid={`pf-benef-${i}`} className="w-full mt-1 rounded border border-gray-200 px-2 py-1.5" />
                                </label>
                                <label>
                                    <span className="text-xs text-gray-500">Currency</span>
                                    <input value={row.currency || "USD"} onChange={(e) => update(i, "currency", e.target.value)} data-testid={`pf-currency-${i}`} className="w-full mt-1 rounded border border-gray-200 px-2 py-1.5" />
                                </label>
                                <label className="col-span-2 md:col-span-4">
                                    <span className="text-xs text-gray-500">Short description</span>
                                    <input value={row.short_desc} onChange={(e) => update(i, "short_desc", e.target.value)} data-testid={`pf-desc-${i}`} className="w-full mt-1 rounded border border-gray-200 px-2 py-1.5" />
                                </label>
                                <label className="flex items-center gap-2 mt-4">
                                    <input type="checkbox" checked={!!row.verified} onChange={(e) => update(i, "verified", e.target.checked)} data-testid={`pf-verified-${i}`} />
                                    <span className="text-xs">Verified</span>
                                </label>
                                <div className="col-span-2 mt-4 flex gap-2">
                                    <button onClick={() => save(row)} disabled={busy} className="gsf-btn-primary text-xs" data-testid={`pf-save-${i}`}><Save size={14} /> Save</button>
                                    <button onClick={() => remove(row.key)} className="gsf-btn-outline text-xs !border-red-200 !text-red-600 hover:!bg-red-50" data-testid={`pf-delete-${i}`}><Trash2 size={14} /> Delete</button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
